/**
 * Field type definitions for connector field mapping
 * Determines how fields should be mapped and if value transformations are needed
 */

export type FieldType = "text" | "number" | "select" | "boolean"

export interface FieldDefinition {
  value: string
  label: string
  category: string
  type: FieldType
  options?: { value: string; label: string }[] // For select fields
}

// Lead field definitions with types
export const LEAD_FIELD_DEFINITIONS: FieldDefinition[] = [
  { value: "email", label: "Email", category: "Basic", type: "text" },
  { value: "phone", label: "Phone", category: "Basic", type: "text" },
  {
    value: "first_name",
    label: "First Name",
    category: "Basic",
    type: "text"
  },
  {
    value: "last_name",
    label: "Last Name",
    category: "Basic",
    type: "text"
  },
  {
    value: "details.loan_purpose",
    label: "Loan Purpose",
    category: "Loan Info",
    type: "select",
    options: [
      { value: "purchase", label: "Purchase" },
      { value: "refinance", label: "Refinance" }
    ]
  },
  {
    value: "details.loan_type",
    label: "Loan Type",
    category: "Loan Info",
    type: "select",
    options: [
      { value: "conventional", label: "Conventional" },
      { value: "fha", label: "FHA" },
      { value: "va", label: "VA" },
      { value: "usda", label: "USDA" }
    ]
  },
  {
    value: "details.loan_amount",
    label: "Loan Amount",
    category: "Loan Info",
    type: "number"
  },
  {
    value: "details.loan_term",
    label: "Loan Term",
    category: "Loan Info",
    type: "number"
  },
  {
    value: "details.credit_score",
    label: "Credit Score",
    category: "Loan Info",
    type: "number"
  },
  {
    value: "details.down_payment",
    label: "Down Payment",
    category: "Loan Info",
    type: "number"
  },
  {
    value: "details.list_price",
    label: "List Price",
    category: "Loan Info",
    type: "number"
  },
  {
    value: "details.property_type",
    label: "Property Type",
    category: "Property",
    type: "select",
    options: [
      { value: "single_family_home", label: "Single Family Home" },
      { value: "townhome", label: "Townhome" },
      { value: "condominium", label: "Condominium" },
      { value: "multi_tenant_home", label: "Multi-Family Home" }
    ]
  },
  {
    value: "details.residency_type",
    label: "Residency Type",
    category: "Property",
    type: "select",
    options: [
      { value: "primary_home", label: "Primary Home" },
      { value: "second_home", label: "Second Home" },
      { value: "rental_home", label: "Investment/Rental" }
    ]
  },
  {
    value: "details.realtor",
    label: "Realtor",
    category: "Property",
    type: "text"
  },
  {
    value: "details.location.city",
    label: "City",
    category: "Location",
    type: "text"
  },
  {
    value: "details.location.state",
    label: "State",
    category: "Location",
    type: "text"
  },
  {
    value: "details.location.zipcode",
    label: "Zip Code",
    category: "Location",
    type: "text"
  },
  {
    value: "details.location.street_address",
    label: "Street Address",
    category: "Location",
    type: "text"
  },
  {
    value: "details.location.county",
    label: "County",
    category: "Location",
    type: "text"
  },
  {
    value: "details.message",
    label: "Message",
    category: "Other",
    type: "text"
  }
]

// Subscription field definitions with types
export const SUBSCRIPTION_FIELD_DEFINITIONS: FieldDefinition[] = [
  { value: "email", label: "Email", category: "Basic", type: "text" },
  { value: "phone", label: "Phone", category: "Basic", type: "text" },
  { value: "first_name", label: "First Name", category: "Basic", type: "text" },
  { value: "last_name", label: "Last Name", category: "Basic", type: "text" },
  { value: "loid", label: "Loan Officer ID", category: "Basic", type: "text" },
  { value: "type", label: "Type", category: "Basic", type: "text" },
  { value: "active", label: "Active", category: "Basic", type: "boolean" },
  {
    value: "quote_info.loan_purpose",
    label: "Loan Purpose",
    category: "Quote Info",
    type: "select",
    options: [
      { value: "purchase", label: "Purchase" },
      { value: "refinance", label: "Refinance" }
    ]
  },
  {
    value: "quote_info.credit_score",
    label: "Credit Score",
    category: "Quote Info",
    type: "number"
  },
  {
    value: "quote_info.property_type",
    label: "Property Type",
    category: "Quote Info",
    type: "select",
    options: [
      { value: "single_family_home", label: "Single Family Home" },
      { value: "townhome", label: "Townhome" },
      { value: "condominium", label: "Condominium" },
      { value: "multi_tenant_home", label: "Multi-Family Home" }
    ]
  },
  {
    value: "quote_info.residency_type",
    label: "Residency Type",
    category: "Quote Info",
    type: "select",
    options: [
      { value: "primary_home", label: "Primary Home" },
      { value: "second_home", label: "Second Home" },
      { value: "rental_home", label: "Investment/Rental" }
    ]
  },
  {
    value: "quote_info.list_price",
    label: "List Price",
    category: "Quote Info",
    type: "number"
  },
  {
    value: "quote_info.loan_amount",
    label: "Loan Amount",
    category: "Quote Info",
    type: "number"
  },
  {
    value: "quote_info.loan_term",
    label: "Loan Term",
    category: "Quote Info",
    type: "number"
  },
  {
    value: "quote_info.loan_type",
    label: "Loan Type",
    category: "Quote Info",
    type: "select",
    options: [
      { value: "conventional", label: "Conventional" },
      { value: "fha", label: "FHA" },
      { value: "va", label: "VA" },
      { value: "usda", label: "USDA" }
    ]
  },
  {
    value: "quote_info.location.state",
    label: "State",
    category: "Location",
    type: "text"
  },
  {
    value: "quote_info.location.city",
    label: "City",
    category: "Location",
    type: "text"
  },
  {
    value: "quote_info.location.zipcode",
    label: "Zip Code",
    category: "Location",
    type: "text"
  },
  {
    value: "quote_info.location.street_address",
    label: "Street Address",
    category: "Location",
    type: "text"
  },
  {
    value: "quote_info.location.county",
    label: "County",
    category: "Location",
    type: "text"
  }
]

export const PENDING_SUBSCRIPTION_FIELD_DEFINITIONS: FieldDefinition[] = [
  ...SUBSCRIPTION_FIELD_DEFINITIONS,
  {
    value: "trigger_rate",
    label: "Trigger Rate",
    category: "Basic",
    type: "number"
  }
]

/**
 * Get field definition by value
 */
export const getFieldDefinition = (
  fieldValue: string,
  dataType: "lead" | "subscription" | "pending_subscription"
): FieldDefinition | undefined => {
  let definitions: FieldDefinition[]

  switch (dataType) {
    case "subscription":
      definitions = SUBSCRIPTION_FIELD_DEFINITIONS
      break
    case "pending_subscription":
      definitions = PENDING_SUBSCRIPTION_FIELD_DEFINITIONS
      break
    default:
      definitions = LEAD_FIELD_DEFINITIONS
  }

  return definitions.find(def => def.value === fieldValue)
}
